import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { useT } from "@/i18n/locale-context";
import { prepareImage } from "@/lib/image-prepare";
import { selectBestFrame, type BurstResult } from "@/lib/burst-select";
import {
  preflightCapture,
  getPreflightQuota,
  type PreflightResult,
} from "@/lib/capture-preflight.functions";
import type { BodyArea } from "@/lib/protocols/definitions";

/**
 * Pro-only capture helpers (Phase 3):
 *  - L3 "Pre-check": send a downsized preview to the AI coach for shooting hints.
 *  - L4 "Guided burst": pick the sharpest frame from 3–5 candidates client-side.
 *
 * Both are opt-in actions; the resulting chosen frame is bubbled up via
 * `onFramePicked` so the parent page can submit it through the normal flow.
 */
export function CaptureProPanel({
  currentDataUrl,
  bodyArea,
  locale,
  onFramePicked,
}: {
  currentDataUrl: string | null;
  bodyArea: BodyArea;
  locale: "fr" | "en" | "nl";
  onFramePicked: (dataUrl: string) => void;
}) {
  const t = useT();
  const preflight = useServerFn(preflightCapture);
  const fetchQuota = useServerFn(getPreflightQuota);
  const [busy, setBusy] = useState<"none" | "preflight" | "burst">("none");
  const [pre, setPre] = useState<PreflightResult | null>(null);
  const [burst, setBurst] = useState<BurstResult | null>(null);
  const [pickedIndex, setPickedIndex] = useState<number | null>(null);
  const [quota, setQuota] = useState<{ used: number; limit: number; remaining: number; resetsAt: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchQuota().then((q) => { if (!cancelled) setQuota(q); }).catch(() => {});
    return () => { cancelled = true; };
  }, [fetchQuota]);

  function formatReset(iso: string): string {
    try {
      const d = new Date(iso);
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`;
    } catch { return ""; }
  }

  async function onPreflight() {
    if (!currentDataUrl) return;
    if (quota && quota.remaining <= 0) {
      toast.error(t("coach.pre.quota.exhausted").replace("{0}", formatReset(quota.resetsAt)));
      return;
    }
    setBusy("preflight");
    setPre(null);
    try {
      const r = await preflight({ data: { imageBase64: currentDataUrl, bodyArea, locale } });
      setPre(r);
      if (r.quota) setQuota(r.quota);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Error";
      if (msg.startsWith("quota_exceeded")) {
        // Refresh quota so the counter shows 0 left.
        fetchQuota().then((q) => setQuota(q)).catch(() => {});
        toast.error(t("coach.pre.quota.exhausted").replace("{0}", quota ? formatReset(quota.resetsAt) : "00:00"));
      } else if (msg === "rate_limited") {
        toast.error(t("coach.pre.err.rate_limited"));
      } else if (msg === "credits_exhausted") {
        toast.error(t("coach.pre.err.credits"));
      } else {
        toast.error(msg);
      }
    } finally { setBusy("none"); }
  }

  async function onBurst(files: FileList | null) {
    if (!files || files.length === 0) return;
    if (files.length > 6) { toast.error("Max 6"); return; }
    setBusy("burst");
    setBurst(null);
    try {
      const dataUrls: string[] = [];
      for (const f of Array.from(files)) {
        const p = await prepareImage(f);
        dataUrls.push(p.dataUrl);
      }
      const r = await selectBestFrame(dataUrls);
      setBurst(r);
      setPickedIndex(r.bestIndex);
      onFramePicked(dataUrls[r.bestIndex]);
      toast.success(t("coach.burst.picked"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally { setBusy("none"); }
  }

  function pickFrame(index: number) {
    if (!burst) return;
    const frame = burst.scores.find((s) => s.index === index);
    if (!frame) return;
    setPickedIndex(index);
    onFramePicked(frame.dataUrl);
  }

  return (
    <div className="mt-4 rounded-2xl border border-accent/30 bg-accent/[0.04] p-4 space-y-3 text-sm">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-accent/15 text-accent">PRO</span>
        <span className="font-semibold">{t("coach.pro.title")}</span>
      </div>

      {/* L3 — Pre-check */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!currentDataUrl || busy !== "none" || (quota?.remaining ?? 1) <= 0}
          onClick={onPreflight}
          className="text-xs rounded-full border border-foreground/20 px-3 py-1.5 hover:bg-foreground/5 disabled:opacity-40 inline-flex items-center gap-1.5"
          title={quota ? t("coach.pre.quota.tooltip").replace("{0}", String(quota.limit)) : undefined}
        >
          <span>{busy === "preflight" ? t("coach.pre.checking") : t("coach.pre.button")}</span>
          {quota && (
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
              quota.remaining === 0 ? "bg-destructive/15 text-destructive"
              : quota.remaining <= 3 ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
              : "bg-foreground/10 text-muted-foreground"
            }`}>
              {quota.remaining}/{quota.limit}
            </span>
          )}
        </button>

        {/* L4 — Guided burst */}
        <label className="text-xs rounded-full border border-foreground/20 px-3 py-1.5 hover:bg-foreground/5 cursor-pointer">
          {busy === "burst" ? t("coach.burst.picking") : t("coach.burst.button")}
          <input
            type="file" accept="image/*" multiple className="hidden"
            disabled={busy !== "none"}
            onChange={(e) => onBurst(e.target.files)}
          />
        </label>
      </div>

      {quota && (
        <p className="text-[11px] text-muted-foreground">
          {quota.remaining === 0
            ? t("coach.pre.quota.exhausted").replace("{0}", formatReset(quota.resetsAt))
            : t("coach.pre.quota.remaining")
                .replace("{0}", String(quota.remaining))
                .replace("{1}", String(quota.limit))
                .replace("{2}", formatReset(quota.resetsAt))}
        </p>
      )}

      {pre && (
        <div className="rounded-lg border border-foreground/10 p-3 bg-background/60">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("coach.pre.score")}</span>
            <span className={`font-bold ${pre.ready ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>{pre.score}/100 · {pre.ready ? t("coach.pre.ready") : t("coach.pre.notready")}</span>
          </div>
          {pre.hints.length > 0 && (
            <ul className="mt-2 list-disc list-inside text-xs space-y-0.5">
              {pre.hints.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          )}
        </div>
      )}

      {burst && (
        <div className="rounded-lg border border-foreground/10 p-3 bg-background/60">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
            {t("coach.burst.result").replace("{0}", String(burst.scores.length)).replace("{1}", String(burst.bestIndex + 1))}
          </p>
          <div className="grid grid-cols-5 gap-1.5">
            {burst.scores.map((s) => {
              const isPicked = (pickedIndex ?? burst.bestIndex) === s.index;
              const isAutoBest = s.index === burst.bestIndex;
              return (
                <button
                  type="button"
                  key={s.index}
                  onClick={() => pickFrame(s.index)}
                  aria-pressed={isPicked}
                  aria-label={`Frame ${s.index + 1}${isAutoBest ? " (auto best)" : ""}`}
                  className={`relative aspect-square rounded-md overflow-hidden border transition ${
                    isPicked
                      ? "border-emerald-500 ring-2 ring-emerald-500/50"
                      : "border-foreground/10 hover:border-foreground/30"
                  }`}
                >
                  <img src={s.dataUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  {isAutoBest && !isPicked && (
                    <span className="absolute top-0.5 left-0.5 text-[8px] uppercase font-bold px-1 py-0.5 rounded bg-emerald-500/80 text-white">auto</span>
                  )}
                  {isPicked && (
                    <span className="absolute top-0.5 left-0.5 text-[8px] uppercase font-bold px-1 py-0.5 rounded bg-emerald-600 text-white">✓</span>
                  )}
                  <span className="absolute bottom-0 inset-x-0 text-[9px] bg-black/60 text-white text-center py-0.5 font-mono">{Math.round(s.sharpness)}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] text-muted-foreground">{t("coach.burst.tap_to_pick")}</p>
        </div>
      )}
    </div>
  );
}